import { authApi } from '../api/endpoints/auth';
import { storageService } from './storageService';
import { LoginRequest, LoginResponse, UserInfo } from '../api/types/auth.types';

class AuthService {
  // Login
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await authApi.login(credentials);

      if (response.success) {
        // Store tokens and user info
        storageService.setAccessToken(response.accessToken);
        storageService.setRefreshToken(response.refreshToken);
        storageService.setUserInfo(response.user as unknown as Record<string, unknown>);
      }

      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API fails
    } finally {
      // Clear all stored data
      storageService.clearAll();
    }
  }

  // Refresh token
  async refreshToken(): Promise<boolean> {
    const refreshToken = storageService.getRefreshToken();

    if (!refreshToken) {
      return false;
    }

    try {
      const response = await authApi.refreshToken(refreshToken);

      if (response.success) {
        storageService.setAccessToken(response.accessToken);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Get stored access token
  getAccessToken(): string | null {
    return storageService.getAccessToken();
  }

  // Get stored refresh token
  getRefreshToken(): string | null {
    return storageService.getRefreshToken();
  }

  // Get current user info
  getCurrentUser(): UserInfo | null {
    return storageService.getUserInfo() as UserInfo | null;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return storageService.isAuthenticated();
  }

  // Handle errors
  private handleError(error: unknown): Error {
    if (typeof error === 'object' && error !== null && 'message' in error) {
      return new Error((error as { message: string }).message);
    }
    return new Error('An unexpected error occurred');
  }
}

export const authService = new AuthService();
